import type { RecordActionMethod } from '@sentra/contracts';

/**
 * What the gate handed the register, held in this tab's memory and nowhere else.
 *
 * **The gate and the register are two routes, so something has to carry a scan across.** An
 * operator who scans a band nobody has registered presses one button and arrives at the
 * registration form with that band already in it; one who presses "view" or "modify" arrives at
 * that person's record. Both are the same movement of one small fact between two screens.
 *
 * **It is memory, and the three alternatives are each worse for the same reason.** A query
 * parameter is the address bar, which `features/persons/table/url-state.ts` already refuses for a
 * search TERM on the ground that a browser's history is a file on a shared terminal's disk, outside
 * the encryption boundary; a pass code is a live bearer token and a person id names somebody, so
 * both are at least as bad as a name. React Router's `navigate(path, { state })` is not a way
 * around that: it stores the value in `history.state`, which browsers write into the session-restore
 * file. `localStorage` is worse still, because it outlives the tab. A module-level value is lost on
 * a reload, and that is the right failure: the operator scans again, and nothing was written down.
 *
 * **The code stored here is always canonical.** Every way into the gate — the wedge, the camera, an
 * uploaded photograph and the dedicated field — resolves through `parsePassCode` and passes
 * `parsed.code`, so a seeded value satisfies `passCodeSchema` and the band field reaches
 * "available" rather than flashing a malformed refusal at somebody who scanned correctly.
 *
 * **It expires, and the expiry is not redundant with the register clearing it.** That clear only
 * runs if the register actually mounts. A staged hand-off that no navigation ever consumed would
 * sit in memory holding a live pass code, and the next time somebody opened the register by hand it
 * would attach one person's band to another person's registration — silently, which is the shape of
 * failure this product cannot have.
 *
 * A module-level value rather than state or a context, for the reason `api/write-clock.ts` gives
 * about its own: it is a fact about this tab rather than about any component, and the screen that
 * writes it unmounts on its way to the screen that reads it.
 */

/** How long a staged hand-off stays good. Generous against a slow mount, short against a stale band. */
export const INTAKE_HANDOFF_MAX_AGE_MS = 120_000;

/** What the gate stages, before it is stamped. */
export type StagedIntake =
  /** Register somebody. `code` is the band to claim, or `null` when there is no band in hand. */
  | {
      readonly kind: 'register';
      readonly code: string | null;
      readonly method: RecordActionMethod;
    }
  /** Open the record the gate resolved, for reading or for amendment. */
  | {
      readonly kind: 'record';
      readonly personId: string;
      readonly editing: boolean;
      readonly method: RecordActionMethod;
    };

/** A staged hand-off, with the instant it was staged. */
export type IntakeHandoff = StagedIntake & { readonly stagedAtMs: number };

let staged: IntakeHandoff | null = null;

/**
 * Records what the gate is handing over. The clock is an argument so a test can place it.
 */
export function stageIntake(intake: StagedIntake, at: number = Date.now()): void {
  staged = { ...intake, stagedAtMs: at };
}

/**
 * What is staged, or `null`.
 *
 * **Non-destructive, deliberately.** The register reads this from a `useState` initializer, and
 * `app/bootstrap.tsx` wraps the application in `<StrictMode>`, which invokes that initializer twice
 * on the first mount. A read that cleared would hand the second invocation nothing — and the `web`
 * harness composes the providers of `app/app.tsx` rather than `bootstrap.tsx`, so it runs without
 * StrictMode and every test would pass while the browser landed on an empty form. Consumption is
 * {@link clearIntake}, called from an effect, where it happens once per mount and not per render.
 *
 * Written as a function of `now` so the boundary has a test that needs no clock, exactly as
 * `withinProjectionCatchUp` is.
 */
export function peekIntake(now: number = Date.now()): IntakeHandoff | null {
  if (staged === null) {
    return null;
  }
  return now - staged.stagedAtMs < INTAKE_HANDOFF_MAX_AGE_MS ? staged : null;
}

/** Forgets what was staged. The register calls this on mount; a test calls it between cases. */
export function clearIntake(): void {
  staged = null;
}
